import { xEmbedFeaturesBase64 } from './xEmbedFeatures'

// Enhanced URL validation with domain allowlist for each platform
const ALLOWED_YOUTUBE_DOMAINS = ['youtube.com', 'www.youtube.com', 'youtu.be', 'm.youtube.com']
const ALLOWED_VIMEO_DOMAINS = ['vimeo.com', 'www.vimeo.com', 'player.vimeo.com']
const ALLOWED_TIKTOK_DOMAINS = ['tiktok.com', 'www.tiktok.com', 'm.tiktok.com']
const ALLOWED_X_DOMAINS = [
  'x.com',
  'www.x.com',
  'twitter.com',
  'www.twitter.com',
  'mobile.twitter.com',
  'm.twitter.com',
]
const ALLOWED_INSTAGRAM_DOMAINS = ['instagram.com', 'www.instagram.com', 'm.instagram.com']
const ALLOWED_FACEBOOK_DOMAINS = ['facebook.com', 'www.facebook.com', 'm.facebook.com', 'web.facebook.com']
const FACEBOOK_ID_PARAMS = ['v', 'story_fbid', 'fbid', 'id']
const FACEBOOK_VIDEO_PATH = /\/(?:videos|reel|watch)\b/
const URL_SCHEME = /^[a-z][a-z\d+.-]*:/i

export const MAX_EMBED_URL_LENGTH = 255

// Accepts bare domains ("cgtrader.com") and only returns http(s) URLs, so
// javascript: or data: links can never reach an href.
export const parseWebUrl = (input) => {
  const trimmed = (input || '').trim()
  if (!trimmed) return null

  try {
    const parsed = new URL(URL_SCHEME.test(trimmed) ? trimmed : `https://${trimmed}`)
    const isWebProtocol = parsed.protocol === 'http:' || parsed.protocol === 'https:'

    return isWebProtocol && parsed.hostname.includes('.') ? parsed.href : null
  } catch {
    return null
  }
}

export const isValidVideoUrl = (url) => {
  try {
    const parsed = new URL(url)
    const isYouTube = ALLOWED_YOUTUBE_DOMAINS.includes(parsed.hostname)
    const isVimeo = ALLOWED_VIMEO_DOMAINS.includes(parsed.hostname)
    const isTikTok = ALLOWED_TIKTOK_DOMAINS.includes(parsed.hostname)
    const isX = ALLOWED_X_DOMAINS.includes(parsed.hostname)
    const isInstagram = ALLOWED_INSTAGRAM_DOMAINS.includes(parsed.hostname)
    const isFacebook = ALLOWED_FACEBOOK_DOMAINS.includes(parsed.hostname)

    return {
      isValid: isYouTube || isVimeo || isTikTok || isX || isInstagram || isFacebook,
      isYouTube,
      isVimeo,
      isTikTok,
      isX,
      isInstagram,
      isFacebook,
    }
  } catch {
    return {
      isValid: false,
      isYouTube: false,
      isVimeo: false,
      isTikTok: false,
      isX: false,
      isInstagram: false,
      isFacebook: false,
    }
  }
}

export const extractYoutubeId = (url) => {
  const match = url.match(/(?:youtube\.com\/(?:watch\?v=|shorts\/|embed\/)|youtu\.be\/)([^&\n?#]+)/)
  return match ? match[1] : null
}

export const extractVimeoId = (url) => {
  const match = url.match(/vimeo\.com\/(\d+)/)
  return match ? match[1] : null
}

export const extractTikTokId = (url) => {
  // TikTok URLs: only accept full URLs like tiktok.com/@user/video/123456789
  // Short URLs (vm.tiktok.com) are not supported for embedding
  const videoMatch = url.match(/\/video\/(\d+)/)
  return videoMatch ? videoMatch[1] : null
}

export const extractXId = (url) => {
  const match = url.match(/\/status\/(\d+)/)
  return match ? match[1] : null
}

export const extractInstagramId = (url) => {
  const match = url.match(/instagram\.com\/(?:[^/?#]+\/)?(?:p|reels?|tv)\/([A-Za-z0-9_-]+)/)
  return match ? match[1] : null
}

// The Facebook embed plugin takes the post URL itself, so the canonical URL is the id.
export const extractFacebookId = (url) => {
  try {
    const parsed = new URL(url)
    const params = new URLSearchParams()

    FACEBOOK_ID_PARAMS.forEach((key) => {
      if (parsed.searchParams.has(key)) params.set(key, parsed.searchParams.get(key))
    })

    const isPostPath = /\/(?:posts|videos|reel|photos?|permalink\.php|story\.php|watch)\b/.test(parsed.pathname)
    if (!isPostPath) return null

    const query = params.toString()
    return `https://www.facebook.com${parsed.pathname}${query ? `?${query}` : ''}`
  } catch {
    return null
  }
}

export const getPlatformFromUrl = (url) => {
  const validation = isValidVideoUrl(url)

  if (validation.isYouTube) return 'youtube'
  if (validation.isVimeo) return 'vimeo'
  if (validation.isTikTok) return 'tiktok'
  if (validation.isX) return 'x'
  if (validation.isInstagram) return 'instagram'
  if (validation.isFacebook) return 'facebook'

  return 'other'
}

export const extractPlatformId = (url, platform) => {
  switch (platform) {
    case 'youtube': return extractYoutubeId(url)
    case 'vimeo': return extractVimeoId(url)
    case 'tiktok': return extractTikTokId(url)
    case 'x': return extractXId(url)
    case 'instagram': return extractInstagramId(url)
    case 'facebook': return extractFacebookId(url)
    default: return null
  }
}

export const normalizeUrl = (url) => {
  const { isValid } = isValidVideoUrl(url)

  if (!isValid) {
    return url
  }

  const platform = getPlatformFromUrl(url)
  const videoId = extractPlatformId(url, platform)

  // Normalize URLs to standard format
  switch (platform) {
    case 'youtube':
      // Convert youtu.be and other formats to standard youtube.com
      return videoId ? `https://www.youtube.com/watch?v=${videoId}` : url
    case 'vimeo':
      return videoId ? `https://player.vimeo.com/video/${videoId}` : url
    case 'tiktok':
      return url
    case 'x':
      return videoId ? `https://x.com/i/status/${videoId}` : url
    case 'instagram':
      return videoId ? `https://www.instagram.com/p/${videoId}/` : url
    case 'facebook':
      return videoId || url
    default:
      return url
  }
}

// Social posts we can embed become iframes; anything else is kept as a plain website link.
export const buildEmbedFromUrl = (webUrl) => {
  const normalizedUrl = normalizeUrl(webUrl)
  const platform = getPlatformFromUrl(normalizedUrl)
  const externalId = extractPlatformId(normalizedUrl, platform)

  if (platform !== 'other' && externalId) {
    return { type: 'video', source: platform, url: normalizedUrl, externalId }
  }

  return { type: 'link', source: 'link', url: webUrl, externalId: webUrl }
}

export const convertToEmbedUrl = (url, externalId, source, embedId) => {
  const isValid = Boolean(source && externalId) || isValidVideoUrl(url).isValid

  if (!isValid) {
    return url
  }

  const platform = source || getPlatformFromUrl(url)
  const videoId = externalId || extractPlatformId(url, platform)

  switch (platform) {
    case 'youtube':
      return videoId ? `https://www.youtube.com/embed/${videoId}` : url
    case 'vimeo':
      return videoId ? `https://player.vimeo.com/video/${videoId}` : url
    case 'tiktok': {
      // Use TikTok Player API v1 with optimized settings for embedded display
      if (!videoId) return url
      const params = new URLSearchParams({
        music_info: '0',        // Hide music info
        description: '0',       // Hide description
        controls: '0',          // Hide playback controls
        rel: '0',               // Show current author's videos, not recommendations
      })
      return `https://www.tiktok.com/player/v1/${videoId}?${params.toString()}`
    }
    case 'x': {
      if (!videoId) return url
      const params = new URLSearchParams({
        id: videoId,
        dnt: 'false',
        embedId,
        features: xEmbedFeaturesBase64,
        frame: 'false',
        hideCard: 'true',
        hideThread: 'true',
        lang: 'en',
        maxWidth: '100%',
        origin: 'https://publish.twitter.com/#',
        theme: 'light',
        widgetsVersion: '2615f7e52b7e0:1702314776716',
        width: '100%',
      })
      return `https://platform.twitter.com/embed/Tweet.html?${params.toString()}`
    }
    case 'instagram':
      return videoId ? `https://www.instagram.com/p/${videoId}/embed` : url
    case 'facebook': {
      if (!videoId) return url
      const plugin = FACEBOOK_VIDEO_PATH.test(videoId) ? 'video' : 'post'
      return `https://www.facebook.com/plugins/${plugin}.php?href=${encodeURIComponent(videoId)}`
    }
    default:
      return url
  }
}
